import { CoreGlobalFooter } from '@care/navigation';
import getConfig from 'next/config';
import { IterableEmbeddedProvider, useOnJwtRequest } from '@care/iterable-display';
import { type SegmentationListingPageProps } from '@/components/feature/searchDirectory/SearchDirectoryProps';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import IterableBanner from '@/components/common/IterableBanner';
import { UI_FEATURE_FLAGS } from '@/constants';
import { SearchDirectoryDataProvider } from '@/context/searchDirectory/SearchDirectoryDataContext';
import { InternalLinkingSection } from '@/components/feature/searchDirectory/InternalLinkingSection';
import { BreadcrumbsSection } from '@/components/feature/searchDirectory/BreadcrumbsSection';
import { ArticleSection } from '@/components/common/ArticleSection/ArticleSection';
import { getArticlesList } from '@/helpers/getArticlesList';
import { ReviewsSection } from '@/components/feature/searchDirectory/ReviewsSection/ReviewsSection';
import { FAQSection } from '@/components/feature/searchDirectory/FAQSection';
import { HeroSection } from './components/section/HeroSection';
import { SearchResultsWithAsideSection } from './components/section/SearchResultsWithAsideSection';

const {
  publicRuntimeConfig: {
    ITERABLE_MFE_API_KEY,
    ITERABLE_MFE_PACKAGE_NAME,
    ITERABLE_MFE_GUEST_USER_ID,
    ITERABLE_TOP_BANNER_PLACEMENT_ID,
  },
} = getConfig();

export const HybridSearchDirectory = ({ pageData }: SegmentationListingPageProps) => {
  const articlesList = getArticlesList(pageData.seoArticlesList);

  const iterableEmbeddedGuestMessagingEnabled = useFeatureFlag(
    UI_FEATURE_FLAGS.ITERABLE_EMBEDDED_GUEST_MESSAGING_ENABLED.id
  );
  const getJWTRequest = useOnJwtRequest(ITERABLE_MFE_GUEST_USER_ID);

  return (
    <IterableEmbeddedProvider
      userId={ITERABLE_MFE_GUEST_USER_ID}
      packageName={ITERABLE_MFE_PACKAGE_NAME}
      apiKey={ITERABLE_MFE_API_KEY}
      placementIds={[ITERABLE_TOP_BANNER_PLACEMENT_ID].map(Number)}
      onJwtRequest={getJWTRequest}
      disabled={!iterableEmbeddedGuestMessagingEnabled?.value}>
      <SearchDirectoryDataProvider pageData={pageData}>
        <IterableBanner placementId={ITERABLE_TOP_BANNER_PLACEMENT_ID} />
        <HeroSection />
        <SearchResultsWithAsideSection />
        <ReviewsSection searchDirectoryTemplateType="HYBRID" />
        <FAQSection />
        <ArticleSection articles={articlesList} />
        <InternalLinkingSection />
        <BreadcrumbsSection />
        <CoreGlobalFooter variant="inverse" />
      </SearchDirectoryDataProvider>
    </IterableEmbeddedProvider>
  );
};

export default HybridSearchDirectory;
